import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDisplayItem
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { DynamicTypeService } from "../../../../services/dynamic-type-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import SymbolDropdownLoading from "./symbol-dropdown-loading";

export interface IRemoveFromBagSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  onSaveAction: (action: IAction, changeReason?: ESdChangeReason) => void;
}

export interface IRemoveFromBagSymbolsGroupState {
  bags: { [key: string]: any };
  bagsLoading?: boolean;
}

class RemoveFromBagSymbolsGroup extends PureSdComponent<
  IRemoveFromBagSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IRemoveFromBagSymbolsGroupState
> {
  constructor(props: IRemoveFromBagSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      bags: {},
      bagsLoading: false
    };
  }

  async componentDidMount() {
    await this.fetchDynamicTypeKeys();
  }

  getLocalDynamicTypes = () => {
    const sd = this.props.sd.getScreenDesign();
    const allDi: IDisplayItem[] = [];
    const localBags: { [key: string]: any } = {};
    if (sd && sd.displayItems) {
      const queue = [...sd.displayItems];
      while (queue.length > 0) {
        const di = queue.shift();
        if (!di) continue;
        allDi.push(di);
        if (di.children && di.children.length > 0) {
          queue.push(...di.children);
        }
      }
    }
    for (const di of allDi) {
      if (!di?.item?.events) continue;
      for (const event of di.item.events) {
        if (!event?.actions) continue;
        for (const action of event.actions) {
          if (action.label === "AddToBag") {
            const keyParam = action.parameters?.find((p) => p.name === "key");
            if (!keyParam) continue;
            const keyValue = keyParam?.symbol?.fixedValue?.toString();
            if (!keyValue || keyValue.trim() === "") continue;
            localBags[keyValue] = { key: keyValue };
          }
        }
      }
    }
    return localBags;
  };

  fetchDynamicTypeKeys = async () => {
    if (this.state.bagsLoading) return;
    this.setState({ bagsLoading: true });
    try {
      const res = await DynamicTypeService.GetDynamicTypes();
      let bags = res?.data?.bags || {};
      const localBags = this.getLocalDynamicTypes();
      bags = { ...bags, ...localBags };
      this.setState({ bags, bagsLoading: false });
      return bags;
    } catch {
      this.setState({ bags: {}, bagsLoading: false });
      return {};
    }
  };

  handleBagKeySelect = (value: string | null): void => {
    // Clearing the field is reported through `onClear`, which writes the same parameter. The
    // selectbox also reports the empty selection here, and acting on it would save twice and
    // put a null in the tree on the way past.
    if (value === null || value === undefined) return;
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = value;
    keyParam.symbol.category = ESymbolCategory.fixed;

    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;

    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  handleBagKeyClear = () => {
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = undefined;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;

    this.props.onSaveAction(action, ESdChangeReason.ChangeSelectedDiByTools);
  };

  getBagKeyValue = (parameters: IActionParameter[]): string => {
    const keyParam = parameters.find((p) => p.name === "key");
    return keyParam?.symbol?.fixedValue;
  };

  renderKey() {
    return (
      <div>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px", marginTop: "8px" }}>
          <ActionParameterTitle parameter={{ name: "Bag Key", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaSelectbox
          searchable
          size="sm"
          allowClear={
            !this.state.bagsLoading &&
            !!this.getBagKeyValue(this.props.parameters) &&
            this.getBagKeyValue(this.props.parameters)?.trim?.() !== ""
          }
          style={{ width: "100%" }}
          value={this.getBagKeyValue(this.props.parameters)}
          onVisibleChange={(open: boolean): void => {
            if (open) this.fetchDynamicTypeKeys();
          }}
          onChange={this.handleBagKeySelect}
          onClear={this.handleBagKeyClear}
          options={(Object.keys(this.state.bags) || []).map((k) => ({ label: k, value: k }))}
          placeholder="Select Bag Key"
          className="kuika_add_actions_inputs_size"
          notFoundContent={this.state.bagsLoading ? <SymbolDropdownLoading /> : undefined}
        />
      </div>
    );
  }

  render() {
    return <div className="kuika_property-grid-column">{this.renderKey()}</div>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };
}

const removeFromBagSymbolsGroup = withContext<IRemoveFromBagSymbolsGroupProps>(
  withSD<IRemoveFromBagSymbolsGroupProps>(RemoveFromBagSymbolsGroup)
);
export { removeFromBagSymbolsGroup as RemoveFromBagSymbolsGroup };
